import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type Readiness = {
  plan?: {
    wipeSchemas?: string[];
    keepTables?: string[];
  };
  readiness?: {
    ok?: boolean;
    checks?: Record<string, boolean>;
    details?: Record<string, unknown>;
  };
};

export function MasterResetPage() {
  const [data, setData] = useState<Readiness | null>(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    const res = await apiFetch('/api/v1/master/system/reset/readiness');
    const body = (await res.json().catch(() => ({}))) as Readiness & {
      message?: string;
      error?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
    setData(body);
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  async function onExecute() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/master/system/reset/execute', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ confirmation: confirm }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        wiped_tables?: string[];
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(
        `RESET completato · ${body.wiped_tables?.length ?? 0} tabelle wipe · email/Twilio preservati`,
      );
      setConfirm('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const checks = data?.readiness?.checks || {};
  const ready = Boolean(data?.readiness?.ok);
  const canExecute = confirm === 'RESET' && ready && !busy;

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Platform RESET"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · wipe dati tenant/plugin. Inviolabili: admin, piani,
            email/Twilio ciphertext, wrap keys, SMS pack catalog. Richiede anche
            CONFIRM_PLATFORM_RESET=1 sul runtime.
          </>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <div className="sk-theme-layout">
        <SectionCard title={ready ? 'READY' : 'NOT READY'}>
          <p className="sk-box__label">Readiness</p>
          <ul className="sk-muted" style={{ marginTop: '0.75rem', paddingLeft: '1.1rem' }}>
            {Object.entries(checks).map(([key, ok]) => (
              <li key={key}>
                {ok ? '✓' : '✗'} {key}
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Schemi / KEEP">
          <p className="sk-box__label">Piano</p>
          <p className="sk-box__body">
            Wipe schemas: {(data?.plan?.wipeSchemas || []).join(', ') || '—'}
          </p>
          <p className="sk-muted" style={{ marginTop: '0.5rem' }}>
            KEEP: {(data?.plan?.keepTables || []).length} tabelle inviolabili
          </p>
        </SectionCard>

        <SectionCard title="Conferma">
          <p className="sk-box__label">Execute</p>
          <div className="sk-theme-fields">
            <label className="sk-field">
              <span>Digita RESET</span>
              <input
                className="sk-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <button
              type="button"
              className="sk-btn"
              disabled={!canExecute}
              onClick={() => void onExecute()}
            >
              {busy ? 'RESET in corso…' : 'Esegui Platform RESET'}
            </button>
          </div>
          <p className="sk-muted" style={{ marginTop: '0.75rem' }}>
            Nessun TRUNCATE CASCADE. Se email/Twilio pre-esistenti non tornano dopo restore, abort.
          </p>
        </SectionCard>
      </div>
    </section>
  );
}
